/**
 * Global Mutation Indicator - TanStack Query v5 Feature
 *
 * V5 FEATURE: useMutationState for global mutation status
 *
 * Shows a global loading indicator when any mutation is running.
 * This is useful for giving users feedback that something is being saved/updated.
 */

import React from 'react';
import { useMutationState } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, Row, Text } from '@/ui';

export function GlobalMutationIndicator(): React.ReactElement | null {
    const { t } = useTranslation();

    const [isVisible, setIsVisible] = React.useState(false);
    const timeoutRef = React.useRef<number | null>(null);

    // V5: useMutationState to see all pending mutations
    const pendingMutations = useMutationState({
        filters: { status: 'pending' },
    });

    React.useEffect(() => {
        const hasPending = pendingMutations.length > 0;

        if (hasPending) {
            // Show immediately when mutation starts
            setIsVisible(true);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        } else if (isVisible) {
            // Hide after a brief delay when mutations complete
            timeoutRef.current = window.setTimeout(() => {
                setIsVisible(false);
            }, 500); // 500ms delay before hiding
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [pendingMutations.length, isVisible]);

    // Show nothing if not visible
    if (!isVisible) {
        return null;
    }
    const mutationCount = pendingMutations.length;
    const message = mutationCount === 1
        ? t('ui.common.saving')
        : t('ui.common.multipleOperations', { count: mutationCount });

    return (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-background/95 backdrop-blur-sm rounded-lg shadow-lg border border-border">
            <Row
                align="center"
                justify="center"
                gap={2}
            >
                <LoadingSpinner size="sm" />
                <Text size="body-sm-responsive" weight="medium">
                    {message}
                </Text>
            </Row>
        </div>
    );
}
